import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';

function formatDate(iso) {
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });
}

export default function RosterVersionPanel({ roster, onClose, onRestored }) {
  const api = useApi();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null); // versionId being restored
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    load();
  }, [roster.id]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/roster/${roster.id}/versions`);
      setVersions(data);
    } catch (err) {
      setError(err.message || 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await api.post(`/roster/${roster.id}/versions/save`, { label: 'Manual Save' });
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save version');
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore(version) {
    if (!window.confirm(
      `Restore to v${version.version_number} "${version.label}"?\n\nThis will replace all current shifts with this version. The roster status will be reset to draft if it was published.`
    )) return;

    setRestoring(version.id);
    setError('');
    try {
      const result = await api.post(`/roster/${roster.id}/versions/${version.id}/restore`, {});
      onRestored(result.shifts);
    } catch (err) {
      setError(err.message || 'Failed to restore version');
      setRestoring(null);
    }
  }

  const isLocked = roster.status === 'locked';

  return (
    <div className="roster-version-panel">
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Version History</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
            Week of {roster.week_start}
          </p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
      </div>

      {/* Save snapshot button */}
      {!isLocked && (
        <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--color-border)' }}>
          <button
            className="btn btn-secondary btn-sm"
            style={{ width: '100%' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><span className="spinner" /> Saving...</> : '+ Save Current Snapshot'}
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ margin: '8px 16px' }}>{error}</div>
      )}

      {/* Version list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <span className="spinner spinner-dark" />
          </div>
        ) : versions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-muted)', fontSize: 14 }}>
            No versions saved yet
          </div>
        ) : (
          versions.map(v => (
            <div
              key={v.id}
              style={{
                padding: '12px 20px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Version number + label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)',
                    background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                    borderRadius: 4, padding: '1px 6px',
                  }}>
                    v{v.version_number}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{v.label}</span>
                </div>

                {/* Wage cost */}
                <div style={{ fontSize: 13, color: 'var(--color-text)', marginBottom: 2 }}>
                  {v.total_wage_cost !== null ? (
                    <>
                      <span style={{ fontWeight: 600 }}>
                        ${parseFloat(v.total_wage_cost).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span style={{ color: 'var(--color-text-muted)' }}> est. wage</span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>No wage data</span>
                  )}
                  {v.missing_rates_count > 0 && (
                    <span style={{
                      marginLeft: 6, fontSize: 11, color: 'var(--color-warning)',
                      background: 'var(--color-warning-bg, #fff8e1)',
                      border: '1px solid var(--color-warning)',
                      borderRadius: 4, padding: '1px 5px',
                    }}>
                      ⚠ {v.missing_rates_count} rate{v.missing_rates_count !== 1 ? 's' : ''} missing
                    </span>
                  )}
                </div>

                {/* Timestamp */}
                <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {formatDate(v.created_at)}
                </div>
              </div>

              {/* Restore button */}
              {!isLocked && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => handleRestore(v)}
                  disabled={restoring !== null}
                >
                  {restoring === v.id ? <span className="spinner" /> : 'Restore'}
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {isLocked && (
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--color-border)',
          fontSize: 12,
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}>
          Roster is locked — restore disabled
        </div>
      )}
    </div>
  );
}
